import {
  Editor,
  Element as SlateElement,
  Node,
  Path,
  Range,
  Transforms
} from 'slate';

import showMessage from '../../utils/showMessage';

type AnyNode = Record<string, unknown> & {type?: string; children?: any[]};

const MIN_COLUMNS = 2;
const MAX_COLUMNS = 6;
const DEFAULT_GAP = 24;
const MIN_GAP = 8;
const MAX_GAP = 72;

const createEmptyParagraph = (): AnyNode => ({
  type: 'paragraph',
  children: [{text: ''} satisfies AnyNode]
});

const isHeaderFooterNode = (node: AnyNode | null | undefined): boolean =>
  !!node && (node.type === 'header-wrapper' || node.type === 'footer-wrapper');

const deepCloneNode = <T,>(node: T): T => JSON.parse(JSON.stringify(node)) as T;

const sanitizeRange = (editor: Editor, range: Range): Range | null => {
  if (!range?.anchor || !range?.focus) return null;
  try {
    if (
      !Editor.hasPath(editor, range.anchor.path) ||
      !Editor.hasPath(editor, range.focus.path)
    ) {
      return null;
    }

    return {
      anchor: Editor.start(editor, range),
      focus: Editor.end(editor, range)
    };
  } catch {
    return null;
  }
};

const buildColumnsContainer = (
  count: number,
  sourceNodes: AnyNode[] = [],
  gap: number = DEFAULT_GAP
): AnyNode => {
  const safeCount = Math.max(
    MIN_COLUMNS,
    Math.min(MAX_COLUMNS, Number(count) || 2)
  );
  const safeGap = Math.max(
    MIN_GAP,
    Math.min(MAX_GAP, Number(gap) || DEFAULT_GAP)
  );
  const sourceWithoutWrappers = sourceNodes.filter(
    (node) => !isHeaderFooterNode(node)
  );
  const clonedNodes = sourceWithoutWrappers.map((node) => deepCloneNode(node));

  const columns: AnyNode[] = [];
  if (clonedNodes.length === 0) {
    for (let i = 0; i < safeCount; i += 1) {
      columns.push({type: 'column', children: [createEmptyParagraph()]});
    }
  } else {
    const chunkBase = Math.floor(clonedNodes.length / safeCount);
    const remainder = clonedNodes.length % safeCount;
    let cursor = 0;

    for (let i = 0; i < safeCount; i += 1) {
      const take = chunkBase + (i < remainder ? 1 : 0);
      const chunk = clonedNodes.slice(cursor, cursor + take);
      cursor += take;
      columns.push({
        type: 'column',
        children: chunk.length > 0 ? chunk : [createEmptyParagraph()]
      });
    }
  }

  return {
    type: 'columns-container',
    columns: safeCount,
    gap: safeGap,
    children: columns
  };
};

interface SelectedTopLevelNodes {
  insertPath: Path;
  entries: Array<{path: Path; node: AnyNode}>;
  nodes: AnyNode[];
}

const getSelectedTopLevelNodes = (
  editor: Editor
): SelectedTopLevelNodes | null => {
  const {selection} = editor;
  if (!selection) return null;
  const safeSelection = sanitizeRange(editor, selection);
  if (!safeSelection) return null;

  const [start, end] = Range.edges(safeSelection);
  if (start.path.length < 2 || end.path.length < 2) return null;

  const startPage = start.path[0];
  const endPage = end.path[0];
  const entries: Array<{path: Path; node: AnyNode}> = [];

  for (let pageIndex = startPage; pageIndex <= endPage; pageIndex += 1) {
    let pageNode: AnyNode | null = null;
    try {
      pageNode = Node.get(editor, [pageIndex]) as AnyNode;
    } catch {
      continue;
    }
    if (!pageNode || !Array.isArray(pageNode.children)) continue;

    const fromIndex = pageIndex === startPage ? start.path[1] : 0;
    const toIndex =
      pageIndex === endPage
        ? end.path[1]
        : Math.max(0, pageNode.children.length - 1);

    for (let childIndex = fromIndex; childIndex <= toIndex; childIndex += 1) {
      const node = pageNode.children[childIndex];
      if (!node || isHeaderFooterNode(node)) continue;
      entries.push({
        path: [pageIndex, childIndex],
        node
      });
    }
  }

  if (entries.length === 0) return null;

  return {
    insertPath: entries[0].path,
    entries,
    nodes: entries.map((entry) => entry.node)
  };
};

const isInsideHeaderFooter = (editor: Editor, at: Range): boolean => {
  const inside = Editor.above(editor, {
    at,
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      (n.type === 'header-wrapper' || n.type === 'footer-wrapper')
  });

  return !!inside;
};

export const insertColumnsInSelection = (
  editor: Editor,
  count: number,
  customGap?: number
): void => {
  const safeSelection = editor.selection
    ? sanitizeRange(editor, editor.selection)
    : null;
  if (safeSelection && isInsideHeaderFooter(editor, safeSelection)) {
    showMessage({
      status: 'warning',
      message:
        'Columns can be inserted in the document body, not inside header/footer.'
    });

    return;
  }

  const selectedBlocks = getSelectedTopLevelNodes(editor);
  if (selectedBlocks) {
    Editor.withoutNormalizing(editor, () => {
      Transforms.deselect(editor);
      const replacementNode = buildColumnsContainer(
        count,
        selectedBlocks.nodes,
        customGap ?? DEFAULT_GAP
      );

      selectedBlocks.entries
        .slice()
        .sort((a, b) => Path.compare(b.path, a.path))
        .forEach((entry) => {
          Transforms.removeNodes(editor, {at: entry.path});
        });

      Transforms.insertNodes(editor, replacementNode as Node, {
        at: selectedBlocks.insertPath
      });
      Transforms.insertNodes(editor, createEmptyParagraph() as Node, {
        at: Path.next(selectedBlocks.insertPath)
      });
    });

    return;
  }

  const columnsContainer = buildColumnsContainer(
    count,
    [],
    customGap ?? DEFAULT_GAP
  );
  Transforms.insertNodes(editor, columnsContainer as Node);
  Transforms.insertNodes(editor, createEmptyParagraph() as Node);
};

export const removeColumns = (editor: Editor): void => {
  Transforms.unwrapNodes(editor, {
    match: (n) =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      n.type === 'columns-container'
  });
};
